import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../hooks/useApi';
import DateInput from '../components/DateInput';
import Icon from '../components/Icon';
import './RegistreActifs.css';

// ─── CONSTANTES ──────────────────────────────────────────────────────────────

const CLASSES_DPA = [
  { label: 'Classe 8 — Équipement général',     value: 'Classe 8',    taux: 20 },
  { label: 'Classe 10 — Véhicules motorisés',   value: 'Classe 10',   taux: 30 },
  { label: 'Classe 10.1 — Véhicules < 36 000$', value: 'Classe 10.1', taux: 30 },
  { label: 'Classe 14 — Brevets / licences',    value: 'Classe 14',   taux: 0  },
  { label: 'Autre — taux personnalisé',         value: 'Autre',       taux: 0  },
];
const PROPRIETAIRES = ['LCU', 'ELA', 'ESCAMEL'];
const SOURCES_EVAL  = ['Estimation personnelle', 'Kijiji / FB Marketplace', 'Expert / Courtier', 'Autre'];
const ANNEE_COURANTE = new Date().getFullYear();
const ANNEES_EVAL    = [2024, 2025, 2026, 2027];

function fmt$(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('fr-CA', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + ' $';
}
function fmtDate(d) {
  if (!d) return '—';
  const mois = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
  const parts = d.split('-');
  if (parts.length < 2) return '—';
  return `${mois[parseInt(parts[1]) - 1]} ${parts[0]}`;
}
function fmtCompact$(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('fr-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + '$';
}
function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

const FORM_VIDE = {
  description: '', proprietaire: 'LCU', date_achat: '',
  cout_acquisition: '', classe_dpa: 'Classe 8', taux_dpa_pct: 20,
  notes: '', passe_en_depense: false,
};
const DISP_VIDE = { statut: 'vendu', date_disposition: '', prix_disposition: '', notes: '' };
const EVAL_VIDE = { annee: String(ANNEE_COURANTE), valeur_marchande: '', source: 'Estimation personnelle', notes: '' };

// ─── COMPOSANT PRINCIPAL ─────────────────────────────────────────────────────

export default function RegistreActifs() {
  const [actifs, setActifs]                     = useState([]);
  const [ongletPrincipal, setOngletPrincipal]   = useState('actifs_dpa');
  const [onglet, setOnglet]                     = useState('actifs');
  const [anneeDpa, setAnneeDpa]                 = useState(String(ANNEE_COURANTE));
  const [lignesDpa, setLignesDpa]               = useState([]);
  const [totauxDpa, setTotauxDpa]               = useState({});
  const [dpaCalculee, setDpaCalculee]           = useState(false);
  const [anneesExistantes, setAnneesExistantes] = useState([]);
  const [filtreSansEvaluation, setFiltreSansEvaluation] = useState(false);

  const [modalActif, setModalActif]             = useState(false);
  const [editActif, setEditActif]               = useState(null);
  const [form, setForm]                         = useState(FORM_VIDE);
  const [fichierDoc, setFichierDoc]             = useState(null);

  const [modalDisp, setModalDisp]               = useState(false);
  const [actifDisp, setActifDisp]               = useState(null);
  const [formDisp, setFormDisp]                 = useState(DISP_VIDE);
  const [gainPerte, setGainPerte]               = useState(null);

  const [modalEval, setModalEval]               = useState(false);
  const [actifEval, setActifEval]               = useState(null);
  const [formEval, setFormEval]                 = useState(EVAL_VIDE);

  const [modalHistorique, setModalHistorique]   = useState(false);
  const [actifHistorique, setActifHistorique]   = useState(null);
  const [evaluationsHist, setEvaluationsHist]   = useState([]);

  const [editDpa, setEditDpa]                   = useState({});
  const [loading, setLoading]                   = useState(false);
  const [erreur, setErreur]                     = useState('');
  const sectionTableauRef                       = useRef(null);

  // ─ Chargement ─────────────────────────────────────────────────────────────
  const chargerActifs = useCallback(async () => {
    try {
      const data = await api.get('/actifs');
      setActifs(data.actifs || []);
    } catch (e) {
      setErreur(e.message || 'Erreur chargement actifs');
    }
  }, []);

  const chargerDpa = useCallback(async (annee) => {
    try {
      const data = await api.get(`/actifs/resume/${annee}`);
      setLignesDpa(data.actifs || []);
      setTotauxDpa(data);
      setDpaCalculee((data.actifs || []).length > 0);
    } catch {
      setLignesDpa([]);
      setDpaCalculee(false);
    }
  }, []);

  const chargerAnnees = useCallback(async () => {
    try {
      const data = await api.get('/actifs/dpa-par-annee');
      setAnneesExistantes(data.annees || []);
    } catch { /* silencieux */ }
  }, []);

  useEffect(() => { chargerActifs(); chargerAnnees(); }, [chargerActifs, chargerAnnees]);
  useEffect(() => { chargerDpa(anneeDpa); }, [anneeDpa, chargerDpa]);

  // ─── Dérivés ──────────────────────────────────────────────────────────────
  const actifsDpa            = actifs.filter(a => !a.passe_en_depense);
  const actifsDepense        = actifs.filter(a =>  a.passe_en_depense);
  const actifsDpaActifs      = actifsDpa.filter(a => a.statut === 'actif');
  const actifsDpaDispos      = actifsDpa.filter(a => a.statut !== 'actif');
  const actifsSansEvaluation = actifs.filter(a => a.statut === 'actif' && a.valeur_marchande_source !== 'evaluation');
  const actifsAvecEvaluation = actifs.filter(a => a.statut === 'actif' && a.valeur_marchande_source === 'evaluation');
  const actifsDpaAffiches    = filtreSansEvaluation
    ? actifsDpaActifs.filter(a => a.valeur_marchande_source !== 'evaluation')
    : actifsDpaActifs;
  const actifsDepenseAffiches = filtreSansEvaluation
    ? actifsDepense.filter(a => a.statut === 'actif' && a.valeur_marchande_source !== 'evaluation')
    : actifsDepense;

  // ─── KPIs ─────────────────────────────────────────────────────────────────
  const kpiNetteDpa     = round2(totauxDpa.total_fnacc_fin || 0);
  const dpaDescriptions = actifsDpaActifs.map(a => a.description).join(' · ');
  const resumeMarchand  = totauxDpa.valeur_marchande || {
    LCU: { actifs_propres: 0, part_escamel: 0, total: 0, detail: [] },
    ELA: { actifs_propres: 0, part_escamel: 0, total: 0, detail: [] },
    total: 0,
    annee: parseInt(anneeDpa, 10),
  };
  const nbSansEvaluation = totauxDpa.nb_actifs_non_evalues ?? actifsSansEvaluation.length;
  const nbAvecEvaluation = totauxDpa.nb_actifs_evalues ?? actifsAvecEvaluation.length;
  const nbActifsTotal    = totauxDpa.nb_actifs_total ?? (nbSansEvaluation + nbAvecEvaluation);

  function activerFiltreSansEvaluation() {
    if (nbSansEvaluation === 0) return;
    setFiltreSansEvaluation(true);
    setOnglet('actifs');
    window.requestAnimationFrame(() => {
      sectionTableauRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function renderValeurMarchandeCell(actif) {
    const anneeEval = actif.valeur_marchande_annee || actif.derniere_evaluation?.annee;
    return (
      <span className="ra-eval-val">
        <span>{fmt$(actif.valeur_marchande)}</span>
        {actif.valeur_marchande_source === 'evaluation' ? (
          <span className="ra-eval-badge ra-eval-badge--gold" title={`Évaluation manuelle ${anneeEval || ''}`.trim()}>
            {anneeEval}
          </span>
        ) : (
          <span
            className="ra-eval-fallback"
            title="Valeur estimée = coût d'acquisition (aucune évaluation saisie)"
            aria-label="Valeur estimée = coût d'acquisition (aucune évaluation saisie)"
          >
            i
          </span>
        )}
      </span>
    );
  }

  // ─── DPA estimée ──────────────────────────────────────────────────────────
  const dpaEstimee = (() => {
    const cout = parseFloat(form.cout_acquisition) || 0;
    const taux = parseFloat(form.taux_dpa_pct) || 0;
    return Math.round(cout * (taux / 100) * 0.5 * 100) / 100;
  })();

  // ─── Modals actif ─────────────────────────────────────────────────────────
  function ouvrirAjout(passeEnDepense = false) {
    setEditActif(null);
    setForm({ ...FORM_VIDE, passe_en_depense: passeEnDepense });
    setFichierDoc(null);
    setModalActif(true);
  }
  function ouvrirEdition(actif) {
    setEditActif(actif);
    setForm({
      description:      actif.description,
      proprietaire:     actif.proprietaire,
      date_achat:       actif.date_achat || '',
      cout_acquisition: String(actif.cout_acquisition),
      classe_dpa:       actif.classe_dpa,
      taux_dpa_pct:     actif.taux_dpa_pct,
      notes:            actif.notes || '',
      passe_en_depense: !!actif.passe_en_depense,
    });
    setFichierDoc(null);
    setModalActif(true);
  }

  async function sauvegarderActif() {
    setLoading(true); setErreur('');
    try {
      const fd = new FormData();
      fd.append('description',      form.description);
      fd.append('proprietaire',     form.proprietaire);
      fd.append('date_achat',       form.date_achat || '');
      fd.append('cout_acquisition', parseFloat(form.cout_acquisition));
      fd.append('classe_dpa',       form.classe_dpa);
      fd.append('taux_dpa_pct',     parseFloat(form.taux_dpa_pct) || 20);
      fd.append('notes',            form.notes || '');
      fd.append('passe_en_depense', form.passe_en_depense ? 1 : 0);
      if (fichierDoc) fd.append('document', fichierDoc);

      if (editActif) await api.putForm(`/actifs/${editActif.id}`, fd);
      else           await api.postForm('/actifs', fd);
      setModalActif(false);
      await chargerActifs();
    } catch (e) {
      setErreur(e.message || 'Erreur sauvegarde');
    } finally {
      setLoading(false);
    }
  }

  async function supprimerDocument(actif) {
    if (!window.confirm(`Supprimer le document de « ${actif.description} » ?`)) return;
    try {
      await api.delete(`/actifs/${actif.id}/document`);
      await chargerActifs();
    } catch (e) {
      alert(e.response?.data?.error || 'Impossible de supprimer le document');
    }
  }

  async function supprimerActif(actif) {
    if (!window.confirm(`Supprimer « ${actif.description} » ?`)) return;
    try {
      await api.delete(`/actifs/${actif.id}`);
      await chargerActifs();
    } catch (e) {
      alert(e.response?.data?.error || 'Impossible de supprimer');
    }
  }

  // ─── Disposition ──────────────────────────────────────────────────────────
  function ouvrirDisposition(actif) {
    setActifDisp(actif);
    setFormDisp(DISP_VIDE);
    setGainPerte(null);
    setModalDisp(true);
  }

  function calculerGainPerte() {
    if (!actifDisp || formDisp.statut !== 'vendu') { setGainPerte(null); return; }
    const prix = parseFloat(formDisp.prix_disposition) || 0;
    const fnacc = actifDisp.cout_acquisition - (actifDisp.dpa_cumulee || 0);
    setGainPerte(prix - fnacc);
  }
  useEffect(calculerGainPerte, [formDisp.prix_disposition, formDisp.statut, actifDisp]);

  async function confirmerDisposition() {
    setLoading(true); setErreur('');
    try {
      await api.post(`/actifs/${actifDisp.id}/disposition`, {
        ...formDisp,
        prix_disposition: formDisp.prix_disposition ? parseFloat(formDisp.prix_disposition) : null,
      });
      setModalDisp(false);
      await chargerActifs();
    } catch (e) {
      setErreur(e.message || 'Erreur disposition');
    } finally {
      setLoading(false);
    }
  }

  // ─── DPA ──────────────────────────────────────────────────────────────────
  async function calculerDpa() {
    setLoading(true); setErreur('');
    try {
      await api.post(`/actifs/calculer-dpa/${anneeDpa}`);
      await chargerDpa(anneeDpa);
      await chargerAnnees();
    } catch (e) {
      setErreur(e.message || 'Erreur calcul DPA');
    } finally {
      setLoading(false);
    }
  }

  async function sauvegarderDpa(ligne) {
    const dpaRec = parseFloat(editDpa[ligne.id_historique]);
    if (isNaN(dpaRec) || dpaRec < 0) { alert('Montant DPA réclamée invalide'); return; }
    setLoading(true);
    try {
      await api.put(`/actifs/dpa/${ligne.id_historique}`, { dpa_reclamee: dpaRec, valide_comptable: false });
      const nouvelEdit = { ...editDpa };
      delete nouvelEdit[ligne.id_historique];
      setEditDpa(nouvelEdit);
      await chargerDpa(anneeDpa);
      await chargerActifs();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur sauvegarde DPA');
    } finally {
      setLoading(false);
    }
  }

  async function validerDpa(ligne) {
    setLoading(true);
    try {
      await api.put(`/actifs/dpa/${ligne.id_historique}`, { dpa_reclamee: ligne.dpa_reclamee, valide_comptable: true });
      await chargerDpa(anneeDpa);
      await chargerActifs();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur validation');
    } finally {
      setLoading(false);
    }
  }

  // ─── Évaluations marchandes ───────────────────────────────────────────────
  function ouvrirEvaluation(actif) {
    setActifEval(actif);
    const anneeDefaut = actif.derniere_evaluation?.annee
      ? String(Math.max(actif.derniere_evaluation.annee, ANNEE_COURANTE))
      : String(ANNEE_COURANTE);
    setFormEval({
      annee:            anneeDefaut,
      valeur_marchande: actif.derniere_evaluation?.valeur_marchande != null
        ? String(actif.derniere_evaluation.valeur_marchande)
        : '',
      source: actif.derniere_evaluation?.source || 'Estimation personnelle',
      notes:  '',
    });
    setModalEval(true);
  }

  async function sauvegarderEvaluation() {
    setLoading(true); setErreur('');
    try {
      await api.post(`/actifs/${actifEval.id}/evaluations`, {
        annee:            parseInt(formEval.annee),
        valeur_marchande: parseFloat(formEval.valeur_marchande),
        source:           formEval.source,
        notes:            formEval.notes || null,
      });
      setModalEval(false);
      await chargerActifs();
    } catch (e) {
      setErreur(e.message || 'Erreur enregistrement évaluation');
    } finally {
      setLoading(false);
    }
  }

  async function ouvrirHistorique(actif) {
    setActifHistorique(actif);
    setModalHistorique(true);
    try {
      const data = await api.get(`/actifs/${actif.id}/evaluations`);
      setEvaluationsHist(data.evaluations || []);
    } catch {
      setEvaluationsHist([]);
    }
  }

  async function supprimerEvaluation(evalId) {
    if (!window.confirm('Supprimer cette évaluation ?')) return;
    try {
      await api.delete(`/actifs/evaluations/${evalId}`);
      const data = await api.get(`/actifs/${actifHistorique.id}/evaluations`);
      setEvaluationsHist(data.evaluations || []);
      await chargerActifs();
    } catch (e) {
      alert(e.response?.data?.error || 'Impossible de supprimer');
    }
  }

  function setClasse(valeur) {
    const cl = CLASSES_DPA.find(c => c.value === valeur);
    setForm(f => ({ ...f, classe_dpa: valeur, taux_dpa_pct: cl?.taux || f.taux_dpa_pct }));
  }

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="ra-page">

      {/* ── En-tête ──────────────────────────────────────────── */}
      <div className="ra-header">
        <div className="ra-header-text">
          <h1 className="page-title">Registre des actifs</h1>
          <p className="page-subtitle muted">Déduction pour amortissement (DPA) — ARC</p>
        </div>
        <div className="ra-header-actions">
          {ongletPrincipal === 'actifs_dpa' && (
            <>
              <button className="btn btn-ghost" onClick={() => ouvrirAjout(false)}>
                <Icon name="ajouter" size={14} /> Ajouter un actif DPA
              </button>
              {onglet === 'dpa' && !dpaCalculee && (
                <button className="btn btn-primary" onClick={calculerDpa} disabled={loading}>
                  <Icon name="recalculer" size={14} /> Calculer DPA {anneeDpa}
                </button>
              )}
            </>
          )}
          {ongletPrincipal === 'depense' && (
            <button className="btn btn-ghost" onClick={() => ouvrirAjout(true)}>
              <Icon name="ajouter" size={14} /> Ajouter un équipement
            </button>
          )}
        </div>
      </div>

      {erreur && <div className="error-inline ra-erreur">{erreur}</div>}

      {/* ── KPIs ─────────────────────────────────────────────── */}
      <div className="ra-kpi-row">
        <div className="card ra-kpi rise-in">
          <div className="kpi-top">
            <span className="kpi-icon"><Icon name="argent" size={18} /></span>
            <span className="kpi-label">Valeur marchande totale</span>
          </div>
          <div className="ra-kpi-val ra-kpi-val--total" style={{ fontFeatureSettings: "'tnum' 1, 'lnum' 1" }}>{fmt$(resumeMarchand.total)}</div>
          <div className="ra-kpi-chips">
            <span className="ra-kpi-chip ra-kpi-chip--muted">basée acq. ({nbSansEvaluation})</span>
            <span className="ra-kpi-chip ra-kpi-chip--accent">évaluée ({nbAvecEvaluation})</span>
          </div>
        </div>
        <div className="card ra-kpi rise-in">
          <div className="kpi-top">
            <span className="kpi-icon"><Icon name="utilisateur" size={18} /></span>
            <span className="kpi-label">Valeur marchande LCU</span>
          </div>
          <div className="ra-kpi-val ra-kpi-val--accent" style={{ fontFeatureSettings: "'tnum' 1, 'lnum' 1" }}>{fmt$(resumeMarchand.LCU.total)}</div>
          <div className="muted" style={{ fontSize: 11 }}>
            Propres: {fmtCompact$(resumeMarchand.LCU.actifs_propres)} · Escamel: {fmtCompact$(resumeMarchand.LCU.part_escamel)}
          </div>
        </div>
        <div className="card ra-kpi rise-in">
          <div className="kpi-top">
            <span className="kpi-icon"><Icon name="utilisateur" size={18} /></span>
            <span className="kpi-label">Valeur marchande ELA</span>
          </div>
          <div className="ra-kpi-val ra-kpi-val--accent" style={{ fontFeatureSettings: "'tnum' 1, 'lnum' 1" }}>{fmt$(resumeMarchand.ELA.total)}</div>
          <div className="muted" style={{ fontSize: 11 }}>
            Propres: {fmtCompact$(resumeMarchand.ELA.actifs_propres)} · Escamel: {fmtCompact$(resumeMarchand.ELA.part_escamel)}
          </div>
        </div>
        <div className="card ra-kpi rise-in">
          <div className="kpi-top">
            <span className="kpi-icon"><Icon name="analytics" size={18} /></span>
            <span className="kpi-label">Valeur comptable nette (DPA)</span>
          </div>
          <div className="ra-kpi-val success" style={{ fontFeatureSettings: "'tnum' 1, 'lnum' 1" }}>{fmt$(kpiNetteDpa)}</div>
          <div className="muted" style={{ fontSize: 11 }}>{dpaDescriptions || 'Aucun actif DPA actif'}</div>
        </div>
        <button
          type="button"
          className={`card ra-kpi ra-kpi-button${nbSansEvaluation > 0 ? ' ra-kpi--warning' : ' ra-kpi--success'} rise-in`}
          onClick={activerFiltreSansEvaluation}
          disabled={nbSansEvaluation === 0}
        >
          <div className="kpi-top">
            <span className="kpi-icon"><Icon name="attention" size={18} /></span>
            <span className="kpi-label">Actifs sans évaluation</span>
          </div>
          <div className="ra-kpi-val" style={{ fontFeatureSettings: "'tnum' 1" }}>{nbSansEvaluation} / {nbActifsTotal}</div>
          <div className="ra-kpi-chips">
            {nbSansEvaluation === 0 ? (
              <span className="badge badge--success">Tous évalués</span>
            ) : (
              <>
                <span className="badge badge--warning">A évaluer</span>
                <span className="ra-kpi-linkhint">Cliquer pour filtrer</span>
              </>
            )}
          </div>
        </button>
      </div>

      {/* ── Onglets principaux ────────────────────────────────── */}
      <div className="ra-onglets">
        <button
          className={`ra-tab${ongletPrincipal === 'actifs_dpa' ? ' active' : ''}`}
          onClick={() => setOngletPrincipal('actifs_dpa')}
        >
          Actifs DPA
        </button>
        <button
          className={`ra-tab${ongletPrincipal === 'depense' ? ' active' : ''}`}
          onClick={() => setOngletPrincipal('depense')}
        >
          Équipements passés en dépense
          <span className="ra-tab-count">{actifsDepense.length}</span>
        </button>
      </div>

      {/* ════════════════════════════════════════════════════════
          ONGLET — ACTIFS DPA
      ════════════════════════════════════════════════════════ */}
      <div ref={sectionTableauRef}>
      {ongletPrincipal === 'actifs_dpa' && (
        <>
          <div className="ra-onglets ra-onglets--sub">
            <button className={`ra-tab${onglet === 'actifs' ? ' active' : ''}`} onClick={() => setOnglet('actifs')}>Actifs</button>
            <button className={`ra-tab${onglet === 'dpa' ? ' active' : ''}`} onClick={() => setOnglet('dpa')}>DPA par année</button>
          </div>

          {onglet === 'actifs' && (
            <>
              {filtreSansEvaluation && (
                <div className="ra-filtre-row">
                  <span className="badge badge--warning">Filtre actif : sans évaluation</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => setFiltreSansEvaluation(false)}>Retirer le filtre</button>
                </div>
              )}

              {actifsDpaAffiches.length === 0 ? (
                <div className="empty-state card ra-vide">Aucun actif amortissable enregistré.</div>
              ) : (
                <div className="card ra-table-card">
                  <table className="esc-table ra-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Propriétaire</th>
                        <th>Date achat</th>
                        <th>Coût</th>
                        <th>Classe</th>
                        <th>Taux</th>
                        <th>Valeur marchande</th>
                        <th>FNACC actuelle</th>
                        <th>Statut</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {actifsDpaAffiches.map(a => (
                        <tr key={a.id}>
                          <td className="ra-desc">{a.description}</td>
                          <td><span className="badge">{a.proprietaire}</span></td>
                          <td className="muted">{fmtDate(a.date_achat)}</td>
                          <td className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(a.cout_acquisition)}</td>
                          <td className="muted">{a.classe_dpa}</td>
                          <td className="muted">{a.taux_dpa_pct}%</td>
                          <td>{renderValeurMarchandeCell(a)}</td>
                          <td className="success" style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(a.cout_acquisition - (a.dpa_cumulee || 0))}</td>
                          <td><span className="badge badge--success">Actif</span></td>
                          <td>
                            <div className="ra-actions">
                              {a.document_url && (
                                <a className="btn btn-ghost btn-sm" href={a.document_url} target="_blank" rel="noreferrer" title="Voir le document">
                                  <Icon name="document" size={13} />
                                </a>
                              )}
                              <button className="btn btn-ghost btn-sm" onClick={() => ouvrirEdition(a)} title="Modifier"><Icon name="modifier" size={13} /></button>
                              <button className="btn btn-ghost btn-sm" onClick={() => ouvrirDisposition(a)} title="Disposition"><Icon name="recalculer" size={13} /></button>
                              <button className="btn btn-danger btn-sm" onClick={() => supprimerActif(a)} title="Supprimer"><Icon name="supprimer" size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {actifsDpaDispos.length > 0 && (
                <>
                  <h3 className="ra-section-title muted">Dispositions</h3>
                  <div className="card ra-table-card">
                    <table className="esc-table ra-table">
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th>Propriétaire</th>
                          <th>Coût</th>
                          <th>FNACC au moment</th>
                          <th>Prix vente</th>
                          <th>Gain / Perte</th>
                          <th>Date</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {actifsDpaDispos.map(a => {
                          const fnacc = a.cout_acquisition - (a.dpa_cumulee || 0);
                          const gp    = a.prix_disposition != null ? a.prix_disposition - fnacc : null;
                          return (
                            <tr key={a.id} className="ra-row-dispos">
                              <td>{a.description}</td>
                              <td><span className="badge">{a.proprietaire}</span></td>
                              <td style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(a.cout_acquisition)}</td>
                              <td style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(fnacc)}</td>
                              <td style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(a.prix_disposition)}</td>
                              <td className={gp != null ? (gp >= 0 ? 'success' : 'danger') : ''} style={{ fontFeatureSettings: "'tnum' 1" }}>
                                {gp != null ? (gp >= 0 ? '+' : '') + fmt$(gp) : '—'}
                              </td>
                              <td className="muted">{fmtDate(a.date_disposition)}</td>
                              <td>
                                <span className={`badge ${a.statut === 'vendu' ? 'badge--warning' : 'badge--muted'}`}>
                                  {a.statut === 'vendu' ? 'Vendu' : 'Au rancart'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}

          {onglet === 'dpa' && (
            <>
              <div className="ra-dpa-controls">
                <label className="muted" style={{ fontSize: 12 }}>Année fiscale</label>
                <select className="ra-select" value={anneeDpa} onChange={e => setAnneeDpa(e.target.value)}>
                  {anneesExistantes.map(a => <option key={a} value={String(a)}>{a}</option>)}
                  {!anneesExistantes.includes(ANNEE_COURANTE) && (
                    <option value={String(ANNEE_COURANTE)}>{ANNEE_COURANTE} (non calculé)</option>
                  )}
                </select>
              </div>

              {!dpaCalculee ? (
                <div className="card ra-dpa-vide">
                  <p className="muted">Aucun calcul DPA pour {anneeDpa}.</p>
                  <button className="btn btn-primary" onClick={calculerDpa} disabled={loading}>
                    <Icon name="recalculer" size={14} /> {loading ? 'Calcul en cours...' : `Calculer la DPA maximale ${anneeDpa}`}
                  </button>
                </div>
              ) : (
                <>
                  <div className="card ra-table-card">
                    <table className="esc-table ra-table">
                      <thead>
                        <tr>
                          <th>Actif</th>
                          <th>Propriétaire</th>
                          <th>Classe</th>
                          <th>FNACC début</th>
                          <th>DPA maximale</th>
                          <th>DPA réclamée</th>
                          <th>FNACC fin</th>
                          <th>Validé</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lignesDpa.map(l => {
                          const enEdition       = editDpa[l.id_historique] !== undefined;
                          const valDpa          = enEdition ? editDpa[l.id_historique] : l.dpa_reclamee;
                          const fnacc_fin_preview = enEdition
                            ? l.fnacc_debut - (parseFloat(editDpa[l.id_historique]) || 0)
                            : l.fnacc_fin;
                          return (
                            <tr key={l.id_historique}>
                              <td className="ra-desc">
                                {l.description}
                                {l.regle_demi_annee && <span className="ra-badge-demi">1/2</span>}
                              </td>
                              <td><span className="badge">{l.proprietaire}</span></td>
                              <td className="muted">{l.classe_dpa}</td>
                              <td className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(l.fnacc_debut)}</td>
                              <td style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(l.dpa_maximale)}</td>
                              <td>
                                <div className="ra-dpa-edit">
                                  <input
                                    type="number" min="0" max={l.dpa_maximale} step="0.01"
                                    className="ra-input-dpa"
                                    value={valDpa}
                                    onChange={e => setEditDpa(ed => ({ ...ed, [l.id_historique]: e.target.value }))}
                                    disabled={l.valide}
                                  />
                                  {enEdition && !l.valide && (
                                    <button className="btn btn-ghost btn-sm" onClick={() => sauvegarderDpa(l)}>
                                      <Icon name="check" size={13} />
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className={fnacc_fin_preview < 0 ? 'danger' : 'success'} style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(fnacc_fin_preview)}</td>
                              <td>
                                {l.valide ? (
                                  <span className="badge badge--success">Validé</span>
                                ) : (
                                  <button className="btn btn-ghost btn-sm" onClick={() => validerDpa(l)} disabled={loading || enEdition}>
                                    Valider
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      {lignesDpa.length > 1 && (
                        <tfoot>
                          <tr className="ra-total-row">
                            <td colSpan={3} className="muted">TOTAL</td>
                            <td className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(totauxDpa.total_fnacc_fin + (totauxDpa.total_dpa_reclamee || 0))}</td>
                            <td style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(totauxDpa.total_dpa_maximale)}</td>
                            <td style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(totauxDpa.total_dpa_reclamee)}</td>
                            <td className="success" style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(totauxDpa.total_fnacc_fin)}</td>
                            <td></td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>

                  <div className="card ra-dpa-note">
                    <span className="warning"><Icon name="attention" size={15} /></span>
                    <span className="muted">
                      La règle de la demi-année ARC (50%) s'applique à la première année de <strong>réclamation</strong>.
                      Reporter une DPA à 0$ n'enclenche pas la règle — elle s'appliquera à la première réclamation effective.
                      Consultez votre comptable avant de finaliser.
                    </span>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════════
          ONGLET — ÉQUIPEMENTS PASSÉS EN DÉPENSE
      ════════════════════════════════════════════════════════ */}
      {ongletPrincipal === 'depense' && (
        <>
          <div className="ra-dep-resume-row">
            <span className="muted">
              {actifsDepense.length} équipement{actifsDepense.length !== 1 ? 's' : ''} —
              coût total : <strong className="gold">{fmt$(actifsDepense.reduce((s, a) => s + a.cout_acquisition, 0))}</strong>
            </span>
            <span className="muted">
              {actifsDepense.filter(a => a.valeur_marchande_source === 'evaluation').length} évalué{actifsDepense.filter(a => a.valeur_marchande_source === 'evaluation').length !== 1 ? 's' : ''},&nbsp;
              {actifsDepense.filter(a => a.valeur_marchande_source !== 'evaluation').length} sans évaluation
            </span>
          </div>

          {filtreSansEvaluation && (
            <div className="ra-filtre-row">
              <span className="badge badge--warning">Filtre actif : sans évaluation</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setFiltreSansEvaluation(false)}>Retirer le filtre</button>
            </div>
          )}

          {actifsDepenseAffiches.length === 0 ? (
            <div className="empty-state card ra-vide">Aucun équipement passé en dépense.</div>
          ) : (
            <div className="card ra-table-card">
              <table className="esc-table ra-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Propriétaire</th>
                    <th>Date achat</th>
                    <th>Coût d'acq.</th>
                    <th>Valeur marchande</th>
                    <th>Dernière éval.</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {actifsDepenseAffiches.map(a => {
                    const ev = a.derniere_evaluation;
                    return (
                      <tr key={a.id}>
                        <td className="ra-desc">{a.description}</td>
                        <td><span className="badge">{a.proprietaire}</span></td>
                        <td className="muted">{a.date_achat ? fmtDate(a.date_achat) : <span className="muted">—</span>}</td>
                        <td className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(a.cout_acquisition)}</td>
                        <td>{renderValeurMarchandeCell(a)}</td>
                        <td className="muted">{ev ? ev.source : "Fallback cout d'acquisition"}</td>
                        <td>
                          <div className="ra-actions">
                            {a.document_url && (
                              <a className="btn btn-ghost btn-sm" href={a.document_url} target="_blank" rel="noreferrer" title="Voir le document">
                                <Icon name="document" size={13} />
                              </a>
                            )}
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => ouvrirEvaluation(a)}
                              title={ev ? "Mettre à jour l'évaluation" : 'Évaluer'}
                            >
                              <Icon name="analytics" size={13} />
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={() => ouvrirHistorique(a)} title="Historique des évaluations">
                              <Icon name="liste" size={13} />
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={() => ouvrirEdition(a)} title="Modifier"><Icon name="modifier" size={13} /></button>
                            <button className="btn btn-danger btn-sm" onClick={() => supprimerActif(a)} title="Supprimer"><Icon name="supprimer" size={13} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="ra-total-row">
                    <td colSpan={3} className="muted">TOTAL</td>
                    <td className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(actifsDepenseAffiches.reduce((s, a) => s + a.cout_acquisition, 0))}</td>
                    <td className="success" style={{ fontFeatureSettings: "'tnum' 1" }}>
                      {fmt$(actifsDepenseAffiches.reduce((s, a) => s + (a.valeur_marchande || 0), 0))}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
      </div>

      {/* ════════════════════════════════════════════════════════
          MODAL — Ajouter / Modifier actif
      ════════════════════════════════════════════════════════ */}
      {modalActif && (
        <div className="ra-modal-overlay" onClick={() => setModalActif(false)}>
          <div className="ra-modal" onClick={e => e.stopPropagation()}>
            <div className="ra-modal-header">
              <h2 className="ra-modal-title">
                {editActif ? "Modifier l'actif" : (form.passe_en_depense ? 'Nouvel équipement passé en dépense' : 'Nouvel actif amortissable')}
              </h2>
              <button className="ra-modal-close" onClick={() => setModalActif(false)} aria-label="Fermer">
                <Icon name="fermer" size={16} />
              </button>
            </div>

            <div className="ra-modal-body">
              <div className="ra-form-grid">
                <div className="ra-field ra-fg-full">
                  <label>Description</label>
                  <input
                    className="ra-input"
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="ex. Fumoir offset 500 gallons"
                  />
                </div>
                <div className="ra-field">
                  <label>Propriétaire</label>
                  <select className="ra-input" value={form.proprietaire} onChange={e => setForm(f => ({ ...f, proprietaire: e.target.value }))}>
                    {PROPRIETAIRES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="ra-field">
                  <label>Date d'achat {form.passe_en_depense && <span className="muted">(optionnelle)</span>}</label>
                  <DateInput className="ra-input" value={form.date_achat} onChange={v => setForm(f => ({ ...f, date_achat: v }))} />
                </div>
                <div className="ra-field">
                  <label>Coût d'acquisition ($)</label>
                  <input
                    type="number" className="ra-input" min="0" step="0.01"
                    value={form.cout_acquisition}
                    onChange={e => setForm(f => ({ ...f, cout_acquisition: e.target.value }))}
                    placeholder="ex. 14000"
                  />
                </div>
                {!form.passe_en_depense && (
                  <>
                    <div className="ra-field">
                      <label>Classe DPA</label>
                      <select className="ra-input" value={form.classe_dpa} onChange={e => setClasse(e.target.value)}>
                        {CLASSES_DPA.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div className="ra-field">
                      <label>Taux DPA (%)</label>
                      <input
                        type="number" className="ra-input" min="0" max="100" step="0.5"
                        value={form.taux_dpa_pct}
                        onChange={e => setForm(f => ({ ...f, taux_dpa_pct: e.target.value }))}
                      />
                    </div>
                  </>
                )}
                <div className="ra-field ra-fg-full">
                  <label>Notes</label>
                  <input className="ra-input" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optionnel" />
                </div>
                <div className="ra-field ra-fg-full">
                  <label className="ra-checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.passe_en_depense}
                      onChange={e => setForm(f => ({ ...f, passe_en_depense: e.target.checked }))}
                    />
                    Passé en dépense (pas de DPA fiscale)
                  </label>
                </div>
                <div className="ra-field ra-fg-full">
                  <label>Document (facture, garantie, contrat...)</label>
                  <input
                    type="file" className="ra-input"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={e => setFichierDoc(e.target.files?.[0] || null)}
                  />
                  {fichierDoc && <span className="ra-doc-nom muted">{fichierDoc.name}</span>}
                  {!fichierDoc && editActif?.document_fichier && (
                    <div className="ra-doc-existant">
                      <a href={editActif.document_url} target="_blank" rel="noreferrer" className="ra-doc-link">
                        <Icon name="document" size={14} /> Voir le document actuel
                      </a>
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => supprimerDocument(editActif)}>
                        Supprimer
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {!form.passe_en_depense && parseFloat(form.cout_acquisition) > 0 && parseFloat(form.taux_dpa_pct) > 0 && (
                <div className="card ra-dpa-estim">
                  <span className="muted" style={{ fontSize: 12 }}>DPA estimée 1re année (règle demi-année) :</span>
                  <span className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>
                    {fmt$(parseFloat(form.cout_acquisition))} x {form.taux_dpa_pct}% x 50% = <strong>{fmt$(dpaEstimee)}</strong>
                  </span>
                </div>
              )}
            </div>

            <div className="ra-modal-footer">
              <button className="btn btn-ghost" onClick={() => setModalActif(false)}>Annuler</button>
              <button
                className="btn btn-primary"
                onClick={sauvegarderActif}
                disabled={loading || !form.description || !form.cout_acquisition}
              >
                {loading ? 'Sauvegarde...' : editActif ? 'Enregistrer' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════
          MODAL — Disposition
      ════════════════════════════════════════════════════════ */}
      {modalDisp && actifDisp && (
        <div className="ra-modal-overlay" onClick={() => setModalDisp(false)}>
          <div className="ra-modal" onClick={e => e.stopPropagation()}>
            <div className="ra-modal-header">
              <h2 className="ra-modal-title">Disposition — {actifDisp.description}</h2>
              <button className="ra-modal-close" onClick={() => setModalDisp(false)} aria-label="Fermer"><Icon name="fermer" size={16} /></button>
            </div>
            <div className="ra-modal-body">
              <div className="ra-disp-type">
                <label className="ra-radio">
                  <input type="radio" name="statut_disp" value="vendu" checked={formDisp.statut === 'vendu'} onChange={() => setFormDisp(f => ({ ...f, statut: 'vendu' }))} />
                  Vendu
                </label>
                <label className="ra-radio">
                  <input type="radio" name="statut_disp" value="mis_au_rancart" checked={formDisp.statut === 'mis_au_rancart'} onChange={() => setFormDisp(f => ({ ...f, statut: 'mis_au_rancart', prix_disposition: '' }))} />
                  Mis au rancart
                </label>
              </div>
              <div className="ra-form-grid">
                <div className="ra-field">
                  <label>Date de disposition</label>
                  <DateInput className="ra-input" value={formDisp.date_disposition} onChange={v => setFormDisp(f => ({ ...f, date_disposition: v }))} />
                </div>
                {formDisp.statut === 'vendu' && (
                  <div className="ra-field">
                    <label>Prix de vente ($)</label>
                    <input type="number" className="ra-input" min="0" step="0.01" value={formDisp.prix_disposition} onChange={e => setFormDisp(f => ({ ...f, prix_disposition: e.target.value }))} placeholder="ex. 8000" />
                  </div>
                )}
                <div className="ra-field ra-fg-full">
                  <label>Notes</label>
                  <input className="ra-input" value={formDisp.notes} onChange={e => setFormDisp(f => ({ ...f, notes: e.target.value }))} />
                </div>
              </div>
              <div className="card ra-disp-resume">
                <div className="ra-disp-ligne">
                  <span className="muted">FNACC au moment de la disposition</span>
                  <span style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(actifDisp.cout_acquisition - (actifDisp.dpa_cumulee || 0))}</span>
                </div>
                {formDisp.statut === 'vendu' && formDisp.prix_disposition && (
                  <>
                    <div className="ra-disp-ligne">
                      <span className="muted">Prix de vente</span>
                      <span style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(parseFloat(formDisp.prix_disposition))}</span>
                    </div>
                    <div className="ra-disp-ligne ra-disp-total">
                      <span>{gainPerte >= 0 ? 'Gain en capital' : 'Perte en capital'}</span>
                      <span className={gainPerte >= 0 ? 'success' : 'danger'} style={{ fontFeatureSettings: "'tnum' 1" }}>
                        {gainPerte >= 0 ? '+' : ''}{fmt$(gainPerte)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="ra-modal-footer">
              <button className="btn btn-ghost" onClick={() => setModalDisp(false)}>Annuler</button>
              <button className="btn btn-danger" onClick={confirmerDisposition} disabled={loading || !formDisp.date_disposition}>
                {loading ? 'Enregistrement...' : 'Confirmer la disposition'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════
          MODAL — Évaluation marchande
      ════════════════════════════════════════════════════════ */}
      {modalEval && actifEval && (
        <div className="ra-modal-overlay" onClick={() => setModalEval(false)}>
          <div className="ra-modal" onClick={e => e.stopPropagation()}>
            <div className="ra-modal-header">
              <h2 className="ra-modal-title">Évaluation marchande</h2>
              <button className="ra-modal-close" onClick={() => setModalEval(false)} aria-label="Fermer"><Icon name="fermer" size={16} /></button>
            </div>
            <div className="ra-modal-body">
              <p className="muted ra-eval-actif-nom">{actifEval.description}</p>
              <div className="ra-form-grid">
                <div className="ra-field">
                  <label>Année</label>
                  <select className="ra-input" value={formEval.annee} onChange={e => setFormEval(f => ({ ...f, annee: e.target.value }))}>
                    {ANNEES_EVAL.map(a => <option key={a} value={String(a)}>{a}</option>)}
                  </select>
                </div>
                <div className="ra-field">
                  <label>Valeur marchande estimée ($)</label>
                  <input type="number" className="ra-input" min="0" step="1" value={formEval.valeur_marchande} onChange={e => setFormEval(f => ({ ...f, valeur_marchande: e.target.value }))} placeholder="ex. 350" />
                </div>
                <div className="ra-field ra-fg-full">
                  <label>Source</label>
                  <select className="ra-input" value={formEval.source} onChange={e => setFormEval(f => ({ ...f, source: e.target.value }))}>
                    {SOURCES_EVAL.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="ra-field ra-fg-full">
                  <label>Notes <span className="muted">(optionnel)</span></label>
                  <textarea className="ra-input ra-eval-notes" value={formEval.notes} onChange={e => setFormEval(f => ({ ...f, notes: e.target.value }))} placeholder="ex. Vu sur Kijiji à 350$" rows={2} />
                </div>
              </div>
              {actifEval.cout_acquisition && formEval.valeur_marchande && (
                <div className="card ra-eval-preview">
                  <span className="muted">Dépréciation vs coût d'acquisition :</span>
                  <span className="danger">
                    {(() => {
                      const cout = actifEval.cout_acquisition;
                      const val  = parseFloat(formEval.valeur_marchande) || 0;
                      const diff = val - cout;
                      const pct  = ((diff / cout) * 100).toFixed(1);
                      return `${diff >= 0 ? '+' : ''}${fmt$(diff)} (${pct >= 0 ? '+' : ''}${pct}%)`;
                    })()}
                  </span>
                </div>
              )}
            </div>
            <div className="ra-modal-footer">
              <button className="btn btn-ghost" onClick={() => setModalEval(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={sauvegarderEvaluation} disabled={loading || !formEval.valeur_marchande}>
                {loading ? 'Enregistrement...' : "Enregistrer l'évaluation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════
          MODAL — Historique des évaluations
      ════════════════════════════════════════════════════════ */}
      {modalHistorique && actifHistorique && (
        <div className="ra-modal-overlay" onClick={() => setModalHistorique(false)}>
          <div className="ra-modal ra-modal--lg" onClick={e => e.stopPropagation()}>
            <div className="ra-modal-header">
              <h2 className="ra-modal-title">Historique des évaluations</h2>
              <button className="ra-modal-close" onClick={() => setModalHistorique(false)} aria-label="Fermer"><Icon name="fermer" size={16} /></button>
            </div>
            <div className="ra-modal-body">
              <p className="muted ra-eval-actif-nom">{actifHistorique.description} — coût d'acq. : {fmt$(actifHistorique.cout_acquisition)}</p>
              {evaluationsHist.length === 0 ? (
                <div className="empty-state">Aucune évaluation enregistrée pour cet actif.</div>
              ) : (
                <table className="esc-table ra-table">
                  <thead>
                    <tr>
                      <th>Année</th>
                      <th>Valeur marchande</th>
                      <th>Variation</th>
                      <th>Source</th>
                      <th>Notes</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {evaluationsHist.map((ev, i) => {
                      const suivante = evaluationsHist[i + 1];
                      const depot    = suivante ? ev.valeur_marchande - suivante.valeur_marchande : null;
                      const pct      = suivante && suivante.valeur_marchande > 0
                        ? ((depot / suivante.valeur_marchande) * 100).toFixed(1)
                        : null;
                      return (
                        <tr key={ev.id}>
                          <td>
                            <span className={`ra-eval-badge ${ev.annee === ANNEE_COURANTE ? 'ra-eval-badge--gold' : 'ra-eval-badge--muted'}`}>
                              {ev.annee}
                            </span>
                          </td>
                          <td className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>{fmt$(ev.valeur_marchande)}</td>
                          <td>
                            {depot !== null ? (
                              <span className={depot >= 0 ? 'success' : 'danger'} style={{ fontFeatureSettings: "'tnum' 1" }}>
                                {depot >= 0 ? '+' : ''}{fmt$(depot)} ({pct >= 0 ? '+' : ''}{pct}%)
                              </span>
                            ) : <span className="muted">—</span>}
                          </td>
                          <td className="muted">{ev.source}</td>
                          <td className="muted">{ev.notes || '—'}</td>
                          <td>
                            <button className="btn btn-danger btn-sm" onClick={() => supprimerEvaluation(ev.id)} title="Supprimer">
                              <Icon name="supprimer" size={13} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="ra-modal-footer">
              <button className="btn btn-ghost" onClick={() => setModalHistorique(false)}>Fermer</button>
              <button className="btn btn-primary" onClick={() => { setModalHistorique(false); ouvrirEvaluation(actifHistorique); }}>
                <Icon name="ajouter" size={14} /> Nouvelle évaluation
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
